import { useEffect, useRef, useState, useCallback } from 'react';
import { useTickerPolling } from './useTickerPolling';
import tickerPollingService from '../services/tickerPollingService';
import { createLogger } from '../utils/debugLogger';

// Create debug logger for this module
const logger = createLogger('tickerUpdates', 'Minute-based ticker updates via polling', false, 'INFO');

/**
 * Hook for ticker updates using the polling channel
 * Provides a consistent minute-based refresh cadence
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enablePolling - Whether to enable polling (default: true)
 * @param {number} options.pollingInterval - Polling interval in milliseconds (defaults to last saved interval or 60000)
 * @param {Function} options.onUpdate - Callback for ticker updates
 * @param {Function} options.onStatusChange - Callback for status changes
 * @returns {Object} Update control and status
 */
export const useTickerUpdates = (options = {}) => {
  const {
    enablePolling = true,
    pollingInterval: pollingIntervalOption,
    onUpdate,
    onStatusChange
  } = options;

  const [status, setStatus] = useState({
    connectionStatus: enablePolling ? 'connecting' : 'disconnected',
    pollingActive: false,
    lastUpdateSource: null,
    lastUpdateTime: null,
    totalUpdates: 0,
    webSocketUpdates: 0,
    pollingUpdates: 0
  });

  const onUpdateRef = useRef(onUpdate);
  const onStatusChangeRef = useRef(onStatusChange);
  const lastUpdateTimeRef = useRef(null);
  const updateCountsRef = useRef({
    total: 0,
    webSocket: 0,
    polling: 0
  });
  const hasCustomPollingInterval = Number.isFinite(pollingIntervalOption);
  const serviceInterval = tickerPollingService.getCurrentInterval ? tickerPollingService.getCurrentInterval() : 60000;
  const initialPollingInterval = hasCustomPollingInterval
    ? pollingIntervalOption
    : serviceInterval || 60000;

  const pollingIntervalRef = useRef(initialPollingInterval);

  // Update refs when callbacks change
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  // Handle ticker updates from any source
  const handleTickerUpdate = useCallback((data = {}) => {
    const hasTickerUpdates = Array.isArray(data.updated_tickers) && data.updated_tickers.length > 0;

    logger.debug('Ticker update received from polling channel', {
      hasUpdatedTickers: hasTickerUpdates,
      updateCount: data?.updated_tickers?.length || 0,
      pollingCycleComplete: data?.pollingCycleComplete ?? false
    });

    if (hasTickerUpdates) {
      updateCountsRef.current.total++;
      updateCountsRef.current.polling++;
    }

    let updateTimestamp = null;

    if (data.latest_update_time) {
      const parsed = new Date(data.latest_update_time);
      if (!isNaN(parsed)) {
        updateTimestamp = parsed;
      }
    }

    if (!updateTimestamp && data.time) {
      const parsed = new Date(data.time);
      if (!isNaN(parsed)) {
        updateTimestamp = parsed;
      }
    }

    if (!updateTimestamp) {
      updateTimestamp = new Date();
    }

    lastUpdateTimeRef.current = updateTimestamp;

    setStatus(prev => ({
      ...prev,
      lastUpdateSource: 'polling',
      lastUpdateTime: lastUpdateTimeRef.current,
      totalUpdates: updateCountsRef.current.total,
      pollingUpdates: updateCountsRef.current.polling,
      webSocketUpdates: 0
    }));

    if (onUpdateRef.current) {
      onUpdateRef.current({
        ...data,
        has_updates: data.has_updates ?? hasTickerUpdates,
        pollingCycleComplete: data.pollingCycleComplete ?? true
      });
    }
  }, []);

  // Polling subscription with delay
  const pollingOptions = {
    enablePolling,
    pollingInterval: hasCustomPollingInterval ? pollingIntervalOption : undefined,
    autoStart: true,
    onUpdate: (data) => handleTickerUpdate(data),
    onPollingStatusChange: (pollingStatus) => {
      setStatus(prev => ({
        ...prev,
        pollingActive: pollingStatus.isPolling
      }));
    }
  };

  const {
    startPolling,
    stopPolling,
    forcePoll,
    setEnabled: setPollingEnabledInternal,
    setPollingInterval: setPollingIntervalInternal,
    pollingStatus
  } = useTickerPolling(pollingOptions);

  useEffect(() => {
    if (Number.isFinite(pollingIntervalOption)) {
      pollingIntervalRef.current = pollingIntervalOption;
    } else {
      const currentInterval = tickerPollingService.getCurrentInterval
        ? tickerPollingService.getCurrentInterval()
        : pollingIntervalRef.current;
      pollingIntervalRef.current = currentInterval;
    }
  }, [pollingIntervalOption]);

  useEffect(() => {
    if (typeof pollingStatus?.pollingIntervalMs === 'number' && !Number.isNaN(pollingStatus.pollingIntervalMs)) {
      pollingIntervalRef.current = pollingStatus.pollingIntervalMs;
    }
  }, [pollingStatus?.pollingIntervalMs]);

  // Update connection status based on polling activity
  useEffect(() => {
    if (!enablePolling) {
      setStatus(prev => ({
        ...prev,
        connectionStatus: 'disconnected',
        pollingActive: false
      }));
      return;
    }

    if (pollingStatus.isPolling) {
      setStatus(prev => ({
        ...prev,
        connectionStatus: 'connected',
        pollingActive: true
      }));
    } else if (pollingStatus.isEnabled) {
      setStatus(prev => ({
        ...prev,
        connectionStatus: 'connecting',
        pollingActive: false
      }));
    } else {
      setStatus(prev => ({
        ...prev,
        connectionStatus: 'disconnected',
        pollingActive: false
      }));
    }
  }, [enablePolling, pollingStatus.isEnabled, pollingStatus.isPolling]);

  // Notify status changes
  useEffect(() => {
    if (onStatusChangeRef.current) {
      onStatusChangeRef.current(status);
    }
  }, [status]);

  // Control functions
  const startUpdates = useCallback((intervalOverride) => {
    if (enablePolling) {
      const interval = intervalOverride ?? pollingIntervalRef.current;
      startPolling(interval);
    }
  }, [enablePolling, startPolling]);

  const stopUpdates = useCallback(() => {
    if (enablePolling) {
      stopPolling();
    }
  }, [enablePolling, stopPolling]);

  const forceUpdate = useCallback(async () => {
    if (enablePolling) {
      await forcePoll();
    }
  }, [enablePolling, forcePoll]);

  const setPollingEnabled = useCallback((enabled) => {
    setPollingEnabledInternal(enabled);
  }, [setPollingEnabledInternal]);

  const setPollingInterval = useCallback((intervalMs) => {
    if (intervalMs == null) {
      return;
    }

    const parsed = Number(intervalMs);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return;
    }

    const normalized = Math.max(1000, Math.floor(parsed));
    pollingIntervalRef.current = normalized;
    setPollingIntervalInternal(normalized);
  }, [setPollingIntervalInternal]);

  return {
    // Status
    status,
    pollingStatus,

    // Control functions
    startUpdates,
    stopUpdates,
    forceUpdate,
    setPollingEnabled,
    setPollingInterval,

    // Convenience properties
    socketStatus: status.connectionStatus,
    isWebSocketConnected: status.connectionStatus === 'connected',
    isPollingActive: status.pollingActive,
    lastUpdateSource: status.lastUpdateSource,
    lastUpdateTime: status.lastUpdateTime,
    totalUpdates: status.totalUpdates,
    webSocketUpdates: status.webSocketUpdates,
    pollingUpdates: status.pollingUpdates
  };
};

export default useTickerUpdates;
